import { DeeplinkLoggerEventName } from '@/constants/logs/deeplink';
import { EPixelEventName } from '@/constants/pixel';
import MedicineDictionaryModel from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import { createMedicineDictionaryDetailUrl } from '@/entities/medicine-dictionary/util';
import { useAirbridgeDynamicLink } from '@/shared/hooks';
import { Client } from '@/types/app';
import { BreadCrumb, ROUTES } from '@/types/common';
import { Magazine, MagazineType } from '@/types/magazine';
import { EIntegratedSearchTabType } from '@/types/search';
import Pixel from '@/utils/pixel/pixel';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import useGetKeywordSearchMagazineInfinite from '../search/magazine/useGetKeywordSearchMagazineInfinite';
import useNavigate from '../useNavigate';
import useMedicineDictionaryItemByPublicId from './useMedicineDictionaryItemByPublicId';

const MAGAZINE_SIZE = 3;

export interface MedicineDictionaryDetailViewModel {
  breadCrumbs: BreadCrumb[];
  medicineDictionaryItemByPublicId?: MedicineDictionaryModel;
  magazines: Magazine[];
  shouldShowMedicineGuideIndication: () => boolean;
  shouldShowMedicineGuideDosage: () => boolean;
  shouldShowMedicineGuideSideEffect: () => boolean;
  shouldShowMedicineGuideStorage: () => boolean;
  shouldShowMagazine: () => boolean;
  shouldShowVideoInfo: () => boolean;
  shouldShowCtaButton: () => boolean;
  onClickCtaButton: () => void;
  onClickMagazine: (magazine: Magazine) => () => void;
  onClickSource: (url: string) => () => void;
  shouldShowMagazineMoreButton: () => boolean;
  onClickTopCta: () => void;
  onClickMore: () => void;
}

interface Props {
  client: Client;
  publicId?: string;
}

const useMedicineDictionaryDetailViewModel = ({ client, publicId }: Props): MedicineDictionaryDetailViewModel => {
  const router = useRouter();
  const navigate = useNavigate();
  const id = publicId || (router.query.id as string);
  const airbridgeDynamicLink = useAirbridgeDynamicLink();
  const { medicineDictionaryItemByPublicId } = useMedicineDictionaryItemByPublicId(id);
  const { magazines } = useGetKeywordSearchMagazineInfinite({
    keyword: medicineDictionaryItemByPublicId?.getSimplifiedName() ?? '',
    size: MAGAZINE_SIZE,
    contentType: MagazineType.ARTICLE,
    enabled: !!medicineDictionaryItemByPublicId?.getBasicInfoName(),
  });

  const breadCrumbs = useMemo(() => {
    return [
      {
        label: '의약품 사전',
        url: `${ROUTES.MEDICINE_DICTIONARY.HOME.path}`,
        logName: 'medicine_dictionary',
      },
      {
        label: medicineDictionaryItemByPublicId?.getBasicInfoName() ?? '',
        url: `${createMedicineDictionaryDetailUrl(medicineDictionaryItemByPublicId)}`,
        logName: 'medicine_dictionary_detail',
      },
    ];
  }, [medicineDictionaryItemByPublicId]);

  const shouldShowMedicineGuideIndication = (): boolean => {
    return (
      !!medicineDictionaryItemByPublicId?.getMedicineGuideIndications() &&
      medicineDictionaryItemByPublicId?.getMedicineGuideIndications()?.length > 0
    );
  };

  const shouldShowMedicineGuideDosage = (): boolean => {
    return (
      !!medicineDictionaryItemByPublicId?.getMedicineGuideDosages() &&
      medicineDictionaryItemByPublicId?.getMedicineGuideDosages()?.length > 0
    );
  };

  const shouldShowMedicineGuideSideEffect = (): boolean => {
    return (
      !!medicineDictionaryItemByPublicId?.getMedicineGuideSideEffects() &&
      medicineDictionaryItemByPublicId?.getMedicineGuideSideEffects()?.length > 0
    );
  };

  const shouldShowMedicineGuideStorage = (): boolean => {
    return (
      !!medicineDictionaryItemByPublicId?.getMedicineGuideStorages() &&
      medicineDictionaryItemByPublicId?.getMedicineGuideStorages()?.length > 0
    );
  };

  const shouldShowMagazine = (): boolean => {
    return magazines.length > 0;
  };

  const shouldShowVideoInfo = (): boolean => {
    return !!medicineDictionaryItemByPublicId?.getVideoLink();
  };

  const shouldShowCtaButton = (): boolean => {
    if (client.isWebView) {
      return false;
    }

    return true;
  };

  const onClickCtaButton = (): void => {
    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {
      ...(medicineDictionaryItemByPublicId?.getPharmaceuticalEffectLiteracy() && {
        category: medicineDictionaryItemByPublicId?.getPharmaceuticalEffectLiteracy(),
      }),
      ...(medicineDictionaryItemByPublicId?.getSimplifiedName() && {
        drug: medicineDictionaryItemByPublicId?.getSimplifiedName(),
      }),
    });

    if (medicineDictionaryItemByPublicId?.isDietMedicine()) {
      airbridgeDynamicLink.navigateWithTracking(DeeplinkLoggerEventName.ClickCtaButton, {
        campaign: 'web_diet_medicine_dictionary_detail',
        content: 'web_footer_btn_service',
        term: '닥터나우로 편하게 진료받기 ',
      });
      return;
    }
    navigate.navigateToTelemedicineCategoriesScreen();
  };

  const onClickMagazine = (magazine: Magazine) => () => {
    navigate.navigateToMagazineDetail({ magazinePid: magazine.magazinePid });
  };

  const onClickMore = () => {
    navigate.navigateToSearchHomeScreen({
      keyword: medicineDictionaryItemByPublicId?.getSimplifiedName() ?? '',
      tab: EIntegratedSearchTabType.Magazine,
    });
  };

  const onClickSource = (url: string) => () => {
    if (client.isWebView) {
      navigate.navigateToExternalBrowser({ url });
      return;
    }

    window.open(url, '_blank');
  };

  const shouldShowMagazineMoreButton = (): boolean => {
    if (!medicineDictionaryItemByPublicId?.getSimplifiedName()) {
      return false;
    }

    if (magazines.length < 3) {
      return false;
    }

    return !/[/@$!%*#?&]/.test(medicineDictionaryItemByPublicId?.getSimplifiedName());
  };

  const onClickTopCta = () => {
    Pixel.trackCustom(EPixelEventName.TopCtaClick, {
      ...(medicineDictionaryItemByPublicId?.getPharmaceuticalEffectLiteracy() && {
        category: medicineDictionaryItemByPublicId?.getPharmaceuticalEffectLiteracy(),
      }),
      ...(medicineDictionaryItemByPublicId?.getSimplifiedName() && {
        drug: medicineDictionaryItemByPublicId?.getSimplifiedName(),
      }),
    });
  };

  useEffect(() => {
    if (medicineDictionaryItemByPublicId) {
      Pixel.trackCustom(EPixelEventName.PageIn, {
        ...(medicineDictionaryItemByPublicId?.getPharmaceuticalEffectLiteracy() && {
          category: medicineDictionaryItemByPublicId?.getPharmaceuticalEffectLiteracy(),
        }),
        ...(medicineDictionaryItemByPublicId?.getSimplifiedName() && {
          drug: medicineDictionaryItemByPublicId?.getSimplifiedName(),
        }),
      });
    }
  }, [medicineDictionaryItemByPublicId]);

  return {
    breadCrumbs,
    medicineDictionaryItemByPublicId,
    magazines: magazines as Magazine[],
    shouldShowMedicineGuideIndication,
    shouldShowMedicineGuideDosage,
    shouldShowMedicineGuideSideEffect,
    shouldShowMedicineGuideStorage,
    shouldShowMagazine,
    shouldShowVideoInfo,
    shouldShowCtaButton,
    onClickCtaButton,
    onClickMagazine,
    onClickSource,
    shouldShowMagazineMoreButton,
    onClickTopCta,
    onClickMore,
  };
};

export default useMedicineDictionaryDetailViewModel;
